import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserCheck, LogIn, LogOut, Plus, Target, Briefcase, ClipboardList, Clock, Check, X } from "lucide-react";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");
const nice = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { hour12: false }) : "-";

const HR_VALID_TABS = ['attendance', 'overtime', 'leave', 'jobdesk', 'kpi'];

export default function HR() {
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get("tab");
  const [tab, setTabState] = useState(
    HR_VALID_TABS.includes(requestedTab) ? requestedTab : 'attendance'
  );
  useEffect(() => {
    const requested = searchParams.get("tab");
    if (HR_VALID_TABS.includes(requested)) setTabState(requested);
  }, [searchParams]);
  const setTab = (value) => {
    if (!HR_VALID_TABS.includes(value)) return;
    setTabState(value);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    }, { replace: true });
  };
  return (
    <div className="page-shell" data-layered-navigation="hr">
      <div data-workspace-redundant-header>
        <h1 className="text-2xl font-extrabold font-display flex items-center gap-2"><UserCheck className="w-6 h-6 text-orange-500"/>HR Center</h1>
        <p className="text-sm text-slate-500">Attendance, Jobdesk, KPI, Overtime, dan performa karyawan</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-sticky-page-tabs="hr">
          <TabsTrigger value="attendance" data-testid="tab-attendance"><LogIn className="w-4 h-4 mr-1"/>Absensi</TabsTrigger>
          <TabsTrigger value="overtime" data-testid="tab-overtime"><Clock className="w-4 h-4 mr-1"/>Lembur</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">Manajemen Libur</TabsTrigger>
          <TabsTrigger value="jobdesk" data-testid="tab-jobdesk"><Briefcase className="w-4 h-4 mr-1"/>Jobdesk</TabsTrigger>
          <TabsTrigger value="kpi" data-testid="tab-kpi"><Target className="w-4 h-4 mr-1"/>KPI</TabsTrigger>
        </TabsList>
        <TabsContent value="attendance"><AttendanceTab/></TabsContent>
        <TabsContent value="overtime"><OvertimeTab/></TabsContent>
        <TabsContent value="leave"><LeaveManagementTab/></TabsContent>
        <TabsContent value="jobdesk"><JobdeskTab/></TabsContent>
        <TabsContent value="kpi"><KPITab/></TabsContent>
      </Tabs>
    </div>
  );
}


function LeaveManagementTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectId, setRejectId] = useState("");
  const [reason, setReason] = useState("");

  async function load() {
    setLoading(true);
    try {
      const res = await api.get("/leave-requests");
      const data = res.data;
      setRows(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memuat pengajuan libur");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function decide(id, decision, rejectionReason = "") {
    try {
      await api.post(`/leave-requests/${id}/decision`, {
        decision,
        reason: rejectionReason
      });
      toast.success(decision === "approved" ? "Pengajuan disetujui" : "Pengajuan ditolak");
      setRejectId("");
      setReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Keputusan gagal disimpan");
    }
  }

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">Manajemen Libur Karyawan</h3>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? "Memuat..." : "Muat ulang"}
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {loading ? "Memuat pengajuan..." : "Belum ada pengajuan libur."}
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const id = r._id || r.id;
            const status = r.status || "pending";
            return (
              <div key={id} className="rounded-lg border p-3 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">
                    {r.employee_name || r.employee_id || "Karyawan"}
                  </span>
                  <Badge>{status}</Badge>
                </div>
                <p className="text-sm">
                  Tanggal: {r.date || r.leave_date || "-"} · Jenis: {r.leave_type || "-"}
                </p>
                <p className="text-sm">Alasan: {r.reason || "-"}</p>

                {status === "pending" && (
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => decide(id, "approved")}>
                      Setujui
                    </Button>

                    {rejectId === id ? (
                      <div className="flex w-full flex-col gap-2 sm:flex-row">
                        <Input
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Alasan penolakan (wajib)"
                        />
                        <Button
                          variant="destructive"
                          disabled={!reason.trim()}
                          onClick={() => decide(id, "rejected", reason.trim())}
                        >
                          Konfirmasi Tolak
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setRejectId("");
                            setReason("");
                          }}
                        >
                          Batal
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="destructive"
                        onClick={() => {
                          setRejectId(id);
                          setReason("");
                        }}
                      >
                        Tolak
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function OvertimeTab() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", date: new Date().toISOString().slice(0,10), hours: 1, reason: "" });

  const load = () => api.get("/overtime").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/employees").then(({ data }) => setEmployees(data)); }, []);

  async function save() {
    if (!form.employee_id || form.hours <= 0) return toast.error("Lengkapi form");
    try { await api.post("/overtime", form); toast.success("Overtime diajukan"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }
  async function approve(o) {
    try { await api.post(`/overtime/${o.id}/approve`); toast.success("Lembur disetujui"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }
  async function reject(o) {
    try { await api.post(`/overtime/${o.id}/reject`, { reason: "" }); toast.success("Lembur ditolak"); load(); }
    catch (e) { toast.error("Gagal"); }
  }

  return (
    <div className="mt-4 space-y-4">
      <Button data-testid="ot-new-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Ajukan Lembur</Button>
      <Card className="p-4">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 border-b">
            <tr><th className="text-left py-2">Karyawan</th><th className="text-left">Tanggal</th><th className="text-right">Jam</th><th className="text-right">Nominal</th><th>Alasan</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(o => (
              <tr key={o.id} className="border-b">
                <td className="py-2 font-medium">{o.employee_name}</td>
                <td>{o.date}</td>
                <td className="text-right num">{o.hours}</td>
                <td className="text-right num">{rupiah(o.amount)}</td>
                <td className="text-xs text-slate-500 max-w-[180px] truncate">{o.reason}</td>
                <td><Badge className={o.status==="approved"?"bg-emerald-500":o.status==="rejected"?"bg-rose-500":o.status==="consumed"?"bg-blue-500":"bg-amber-500"}>{o.status}</Badge></td>
                <td className="text-right space-x-1">
                  {o.status === "pending" && <>
                    <Button size="sm" onClick={()=>approve(o)} className="bg-emerald-500 hover:bg-emerald-600"><Check className="w-3 h-3"/></Button>
                    <Button size="sm" variant="outline" onClick={()=>reject(o)} className="text-rose-600"><X className="w-3 h-3"/></Button>
                  </>}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="7" className="py-8 text-center text-slate-400">Belum ada pengajuan lembur</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajukan Lembur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs">Karyawan</label>
              <Select value={form.employee_id} onValueChange={v=>setForm({...form,employee_id:v})}>
                <SelectTrigger data-testid="ot-emp-select"><SelectValue placeholder="Pilih"/></SelectTrigger>
                <SelectContent>{employees.map(e=><SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs">Tanggal</label><Input data-testid="ot-date-input" type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})}/></div>
            <div><label className="text-xs">Jam Lembur (0-12)</label><Input data-testid="ot-hours-input" type="number" step="0.5" value={form.hours} onChange={e=>setForm({...form,hours:Number(e.target.value)})}/></div>
            <div><label className="text-xs">Alasan</label><Input value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} placeholder="Contoh: stok bulanan, event"/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="ot-save-btn" onClick={save} className="bg-orange-500 hover:bg-orange-600">Ajukan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AttendanceTab() {
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [summary, setSummary] = useState({ rows: [] });
  const [selectedEmp, setSelectedEmp] = useState("");
  const [note, setNote] = useState("");

  const load = () => {
    api.get("/employees").then(({ data }) => setEmployees(data));
    api.get("/attendance?limit=50").then(({ data }) => setAttendance(data));
    api.get("/attendance/summary?period=30d").then(({ data }) => setSummary(data));
  };
  useEffect(() => { load(); }, []);

  async function clockIn() {
    if (!selectedEmp) return toast.error("Pilih karyawan dulu");
    try {
      await api.post("/attendance/clock-in", { employee_id: selectedEmp, note });
      toast.success("Clock-in tercatat ✓");
      setNote("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  async function clockOut(a) {
    try {
      await api.post("/attendance/clock-out", { attendance_id: a.id });
      toast.success("Clock-out ✓");
      load();
    } catch (e) { toast.error("Gagal"); }
  }

  return (
    <div className="space-y-4 mt-4">
      <Card className="p-4 space-y-3">
        <div className="font-semibold">Clock-in Karyawan</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select value={selectedEmp} onValueChange={setSelectedEmp}>
            <SelectTrigger data-testid="att-employee-select"><SelectValue placeholder="Pilih karyawan"/></SelectTrigger>
            <SelectContent>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={note} onChange={e=>setNote(e.target.value)} placeholder="Catatan (opsional)"/>
          <Button data-testid="att-clockin-btn" onClick={clockIn} className="bg-orange-500 hover:bg-orange-600"><LogIn className="w-4 h-4 mr-1"/>Clock-in</Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="font-semibold mb-2">Riwayat Absensi (50 terbaru)</div>
          <div className="max-h-[500px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b sticky top-0 bg-white">
                <tr><th className="text-left py-2">Karyawan</th><th className="text-left">Masuk</th><th className="text-left">Keluar</th><th className="text-right">Jam</th><th></th></tr>
              </thead>
              <tbody>
                {attendance.map(a => (
                  <tr key={a.id} className="border-b">
                    <td className="py-2 font-medium">{a.employee_name}</td>
                    <td className="text-xs">{nice(a.clock_in)}</td>
                    <td className="text-xs">{a.clock_out ? nice(a.clock_out) : <Badge className="bg-amber-500">Open</Badge>}</td>
                    <td className="text-right num">{a.hours ? a.hours.toFixed(1) : "-"}</td>
                    <td className="text-right">{!a.clock_out && <Button size="sm" variant="outline" onClick={()=>clockOut(a)}><LogOut className="w-3 h-3 mr-1"/>Out</Button>}</td>
                  </tr>
                ))}
                {!attendance.length && <tr><td colSpan="5" className="py-8 text-center text-slate-400">Belum ada absensi</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-4">
          <div className="font-semibold mb-2">Rekap 30 Hari (per karyawan)</div>
          <div className="max-h-[500px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b sticky top-0 bg-white">
                <tr><th className="text-left py-2">Karyawan</th><th className="text-right">Hari</th><th className="text-right">Total Jam</th><th className="text-right">Open</th></tr>
              </thead>
              <tbody>
                {summary.rows?.map(r => (
                  <tr key={r.employee_id} className="border-b">
                    <td className="py-2 font-medium">{r.employee_name}</td>
                    <td className="text-right num">{r.days}</td>
                    <td className="text-right num">{r.hours?.toFixed(1)}</td>
                    <td className="text-right"><Badge variant="outline">{r.open}</Badge></td>
                  </tr>
                ))}
                {!summary.rows?.length && <tr><td colSpan="4" className="py-8 text-center text-slate-400">-</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function JobdeskTab() {
  const [positions, setPositions] = useState([]);
  const [selectedPos, setSelectedPos] = useState(null);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");

  useEffect(() => { api.get("/positions").then(({ data }) => setPositions(data)); }, []);

  function selectPos(p) {
    setSelectedPos(p);
    setItems(p.jobdesk || []);
  }
  function addItem() { if (!newItem.trim()) return; setItems([...items, newItem.trim()]); setNewItem(""); }
  function removeItem(i) { setItems(items.filter((_, idx) => idx !== i)); }
  async function save() {
    if (!selectedPos) return;
    await api.put(`/positions/${selectedPos.id}`, { jobdesk: items });
    toast.success("Jobdesk tersimpan");
    api.get("/positions").then(({ data }) => setPositions(data));
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
      <Card className="p-4 lg:col-span-1">
        <div className="font-semibold mb-2 flex items-center gap-2"><ClipboardList className="w-4 h-4"/>Posisi</div>
        {!positions.length && <div className="text-sm text-slate-400 py-4">Belum ada posisi. Tambahkan di Master Data.</div>}
        <div className="space-y-1">
          {positions.map(p => (
            <button data-testid={`jd-pos-${p.id}`} key={p.id} onClick={()=>selectPos(p)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm ${selectedPos?.id===p.id ? "bg-orange-500 text-white":"hover:bg-slate-100"}`}>
              {p.title} <span className="text-xs opacity-70">L{p.level}</span>
              {p.jobdesk?.length > 0 && <span className="ml-2 text-xs">· {p.jobdesk.length} item</span>}
            </button>
          ))}
        </div>
      </Card>
      <Card className="p-4 lg:col-span-2">
        <div className="font-semibold mb-2">Jobdesk {selectedPos ? `— ${selectedPos.title}` : ""}</div>
        {selectedPos ? (
          <>
            <div className="space-y-2 mb-3">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                  <span className="text-slate-400 text-sm w-6">{i+1}.</span>
                  <span className="flex-1 text-sm">{it}</span>
                  <Button size="sm" variant="outline" onClick={()=>removeItem(i)} className="text-rose-600">×</Button>
                </div>
              ))}
              {!items.length && <div className="text-sm text-slate-400 py-2">Belum ada jobdesk</div>}
            </div>
            <div className="flex gap-2">
              <Input data-testid="jd-new-input" value={newItem} onChange={e=>setNewItem(e.target.value)} placeholder="Tambah tugas baru..." onKeyDown={e=>e.key==="Enter"&&addItem()}/>
              <Button onClick={addItem} variant="outline"><Plus className="w-4 h-4"/></Button>
              <Button data-testid="jd-save-btn" onClick={save} className="bg-orange-500 hover:bg-orange-600">Simpan</Button>
            </div>
          </>
        ) : <div className="text-sm text-slate-400 py-8 text-center">Pilih posisi di kiri untuk mengelola jobdesk</div>}
      </Card>
    </div>
  );
}

function KPITab() {
  const [templates, setTemplates] = useState([]);
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [openT, setOpenT] = useState(false);
  const [openR, setOpenR] = useState(false);
  const [tForm, setTForm] = useState({ name: "", position_id: "", unit: "count", target: 0, weight: 1 });
  const period = new Date().toISOString().slice(0, 7);
  const [rForm, setRForm] = useState({ employee_id: "", template_id: "", actual: 0, note: "", period });

  const load = useCallback(() => {
    api.get("/kpi/templates").then(({ data }) => setTemplates(data));
    api.get(`/kpi/records?period=${period}`).then(({ data }) => setRecords(data));
  }, [period]);

  useEffect(() => {
    load();
    api.get("/employees").then(({ data }) => setEmployees(data));
    api.get("/positions").then(({ data }) => setPositions(data));
  }, [load]);

  async function saveTemplate() {
    if (!tForm.name) return toast.error("Nama KPI wajib");
    await api.post("/kpi/templates", { ...tForm, position_id: tForm.position_id || null });
    toast.success("Template KPI tersimpan");
    setOpenT(false); setTForm({ name: "", position_id: "", unit: "count", target: 0, weight: 1 });
    load();
  }
  async function saveRecord() {
    if (!rForm.employee_id || !rForm.template_id) return toast.error("Lengkapi karyawan & KPI");
    await api.post("/kpi/records", rForm);
    toast.success("KPI actual tersimpan");
    setOpenR(false); setRForm({ ...rForm, employee_id: "", template_id: "", actual: 0, note: "" });
    load();
  }

  return (
    <div className="space-y-4 mt-4">
      <div className="flex gap-2">
        <Button data-testid="kpi-new-template-btn" onClick={()=>setOpenT(true)} variant="outline"><Plus className="w-4 h-4 mr-1"/>Template Baru</Button>
        <Button data-testid="kpi-new-record-btn" onClick={()=>setOpenR(true)} className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Input Actual</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="font-semibold mb-2">Templates KPI</div>
          <div className="space-y-2 max-h-[500px] overflow-auto">
            {templates.map(t => (
              <div key={t.id} className="p-3 rounded-lg bg-slate-50 border">
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-slate-500">Target: <b>{t.target}</b> {t.unit} · Bobot: {t.weight}</div>
                <div className="text-xs text-slate-500">Posisi: {positions.find(p=>p.id===t.position_id)?.title || "Semua"}</div>
              </div>
            ))}
            {!templates.length && <div className="text-sm text-slate-400 py-4">Belum ada template</div>}
          </div>
        </Card>

        <Card className="p-4">
          <div className="font-semibold mb-2">Records ({period})</div>
          <div className="space-y-2 max-h-[500px] overflow-auto">
            {records.map(r => (
              <div key={r.id} className="p-3 rounded-lg bg-slate-50 border">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{employees.find(e=>e.id===r.employee_id)?.name || r.employee_id}</div>
                    <div className="text-xs text-slate-500">{r.template_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm num">{r.actual} / {r.target}</div>
                    <Badge className={r.achievement_pct >= 100 ? "bg-emerald-500" : r.achievement_pct >= 70 ? "bg-amber-500" : "bg-rose-500"}>
                      {r.achievement_pct}%
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
            {!records.length && <div className="text-sm text-slate-400 py-4">Belum ada input</div>}
          </div>
        </Card>
      </div>

      <Dialog open={openT} onOpenChange={setOpenT}>
        <DialogContent>
          <DialogHeader><DialogTitle>Template KPI Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs">Nama KPI</label><Input data-testid="kpi-name-input" value={tForm.name} onChange={e=>setTForm({...tForm,name:e.target.value})} placeholder="Target Omzet Bulanan"/></div>
            <div><label className="text-xs">Posisi (opsional)</label>
              <Select value={tForm.position_id || "all"} onValueChange={v=>setTForm({...tForm,position_id: v==="all"?"":v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua posisi</SelectItem>{positions.map(p=><SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs">Unit</label>
                <Select value={tForm.unit} onValueChange={v=>setTForm({...tForm,unit:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="count">Jumlah</SelectItem><SelectItem value="rupiah">Rupiah</SelectItem><SelectItem value="percent">Persentase</SelectItem></SelectContent>
                </Select>
              </div>
              <div><label className="text-xs">Target</label><Input data-testid="kpi-target-input" type="number" value={tForm.target} onChange={e=>setTForm({...tForm,target:Number(e.target.value)})}/></div>
            </div>
            <div><label className="text-xs">Bobot</label><Input type="number" step="0.1" value={tForm.weight} onChange={e=>setTForm({...tForm,weight:Number(e.target.value)})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpenT(false)}>Batal</Button><Button data-testid="kpi-save-template-btn" onClick={saveTemplate} className="bg-orange-500 hover:bg-orange-600">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openR} onOpenChange={setOpenR}>
        <DialogContent>
          <DialogHeader><DialogTitle>Input Actual KPI · {period}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs">Karyawan</label>
              <Select value={rForm.employee_id} onValueChange={v=>setRForm({...rForm,employee_id:v})}>
                <SelectTrigger data-testid="kpi-emp-select"><SelectValue placeholder="Pilih"/></SelectTrigger>
                <SelectContent>{employees.map(e=><SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs">Template KPI</label>
              <Select value={rForm.template_id} onValueChange={v=>setRForm({...rForm,template_id:v})}>
                <SelectTrigger data-testid="kpi-tmpl-select"><SelectValue placeholder="Pilih"/></SelectTrigger>
                <SelectContent>{templates.map(t=><SelectItem key={t.id} value={t.id}>{t.name} (target {t.target})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs">Actual</label><Input data-testid="kpi-actual-input" type="number" value={rForm.actual} onChange={e=>setRForm({...rForm,actual:Number(e.target.value)})}/></div>
            <div><label className="text-xs">Catatan</label><Input value={rForm.note} onChange={e=>setRForm({...rForm,note:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpenR(false)}>Batal</Button><Button data-testid="kpi-save-record-btn" onClick={saveRecord} className="bg-orange-500 hover:bg-orange-600">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
